import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, ScanLine, Upload, X, Send, Save, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TFS = ['D1', 'H4', 'H1', 'M30', 'M15'];
const card = { backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' };
const verdictColor = { BUY: '#059669', SELL: '#DC2626', WAIT: 'var(--primary-color)' };

function UploadSlots({ slots, setSlots }) {
    const inputs = useRef({});
    const set = (tf, file) => setSlots((s) => ({ ...s, [tf]: file || undefined }));
    return (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="analyst-upload-slots">
            {TFS.map((tf) => (
                <div key={tf} className="rounded-xl p-3 text-center" style={card} data-testid={`analyst-slot-${tf}`}>
                    <p className="text-xs font-black mb-2" style={{ color: 'var(--text-primary)' }}>{tf}</p>
                    <input ref={(el) => (inputs.current[tf] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => set(tf, e.target.files?.[0])} data-testid={`analyst-file-${tf}`} />
                    {slots[tf] ? (
                        <div className="relative">
                            <img src={URL.createObjectURL(slots[tf])} alt={tf} className="w-full h-20 object-cover rounded-lg" />
                            <button type="button" onClick={() => set(tf, null)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                        </div>
                    ) : (
                        <button type="button" onClick={() => inputs.current[tf]?.click()} className="w-full h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-[11px]" style={{ borderColor: 'var(--border-highlight)', color: 'var(--text-muted)' }}>
                            <Upload className="w-4 h-4 mb-1" /> Add chart
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

function DraftEditor({ job, onPublished }) {
    const r = job.result || {};
    const [form, setForm] = useState({ direction: r.direction || 'BUY', entry_price: r.entry_price || '', stop_loss: r.stop_loss || '', take_profit: r.take_profit || '', reasoning: r.reasoning || '', setup_type: r.setup_type || '' });
    const [imageUrl, setImageUrl] = useState(job.image_url);
    const [busy, setBusy] = useState('');
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const payload = () => ({ ...form, entry_price: Number(form.entry_price), stop_loss: Number(form.stop_loss), take_profit: Number(form.take_profit) });

    const save = async () => {
        setBusy('save');
        try {
            const { data } = await axios.put(`${API}/admin/engine/drafts/${job.draft_id}`, payload());
            setImageUrl(data.image_url); toast.success('Draft updated & chart re-rendered');
        } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
        setBusy('');
    };
    const publish = async () => {
        if (!window.confirm('Publish this analysis to students + Telegram?')) return;
        setBusy('publish');
        try {
            await axios.put(`${API}/admin/engine/drafts/${job.draft_id}`, payload());
            await axios.post(`${API}/admin/engine/drafts/${job.draft_id}/approve`);
            toast.success('Published to students & Telegram'); onPublished?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
        setBusy('');
    };

    return (
        <div className="space-y-4" data-testid="analyst-draft-editor">
            {imageUrl && <img src={`${API}/files/${imageUrl}`} alt="Annotated analysis" className="w-full rounded-xl border" style={{ borderColor: 'var(--border-subtle)' }} data-testid="analyst-chart-image" />}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><label className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Direction</label>
                    <select value={form.direction} onChange={upd('direction')} className="input-field w-full h-10 rounded-md px-2" data-testid="draft-direction"><option>BUY</option><option>SELL</option></select></div>
                {[['entry_price', 'Entry'], ['stop_loss', 'Stop Loss'], ['take_profit', 'Take Profit']].map(([k, l]) => (
                    <div key={k}><label className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>{l}</label>
                        <Input type="number" step="any" value={form[k]} onChange={upd(k)} className="input-field" data-testid={`draft-${k}`} /></div>
                ))}
            </div>
            <div><label className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Setup type</label><Input value={form.setup_type} onChange={upd('setup_type')} className="input-field" data-testid="draft-setup-type" /></div>
            <div><label className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Educational write-up (sent as notes)</label><Textarea rows={6} value={form.reasoning} onChange={upd('reasoning')} className="input-field" data-testid="draft-reasoning" /></div>
            <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={save} disabled={!!busy} data-testid="draft-save-btn">{busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save & re-render chart</Button>
                <Button className="btn-primary" onClick={publish} disabled={!!busy} data-testid="draft-publish-btn">{busy === 'publish' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Publish to students + Telegram</Button>
            </div>
        </div>
    );
}

function Result({ job, onPublished }) {
    const r = job.result || {};
    const v = r.verdict || (r.setup_found ? r.direction : 'WAIT');
    return (
        <div className="rounded-2xl p-6 space-y-4" style={card} data-testid="analyst-result">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-xl font-black" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>{job.pair}</h3>
                <span className="px-3 py-1 rounded-full text-sm font-black text-white" style={{ backgroundColor: verdictColor[v] || 'var(--primary-color)' }} data-testid="analyst-verdict">{v}{typeof r.confidence === 'number' ? ` · ${r.confidence}%` : ''}</span>
            </div>
            {[['Market profile', r.market_profile], ['Liquidity', r.liquidity], ['Reasoning', r.reasoning], ['Wait for / confirmation', r.wait_for], ['Risk note', r.risk_note]].filter(([, x]) => x).map(([l, x]) => (
                <div key={l}><p className="text-[11px] font-black uppercase tracking-wide mb-0.5" style={{ color: 'var(--primary-color)' }}>{l}</p><p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>{x}</p></div>
            ))}
            {!!(r.key_levels || []).length && (
                <div><p className="text-[11px] font-black uppercase tracking-wide mb-1" style={{ color: 'var(--primary-color)' }}>Key levels</p>
                    <div className="flex flex-wrap gap-2">{r.key_levels.map((k, i) => <span key={i} className="px-2 py-1 rounded-md text-xs font-mono" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--text-primary)' }}>{k.label}: {k.price}</span>)}</div></div>
            )}
            {job.draft_id ? <DraftEditor job={job} onPublished={onPublished} /> : (
                <p className="text-sm rounded-xl p-3" style={{ backgroundColor: 'rgba(184,134,11,0.08)', color: 'var(--text-secondary)' }} data-testid="analyst-no-setup">No tradeable setup right now — nothing to publish. Re-run when the confirmation above appears.</p>
            )}
        </div>
    );
}

export default function AdminAnalyst() {
    const [pair, setPair] = useState('');
    const [note, setNote] = useState('');
    const [slots, setSlots] = useState({});
    const [job, setJob] = useState(null);
    const [history, setHistory] = useState([]);
    const pollRef = useRef(null);
    const loadHistory = () => axios.get(`${API}/admin/analyst/jobs?limit=8`).then((r) => setHistory(r.data)).catch(() => {});
    useEffect(() => { loadHistory(); return () => clearInterval(pollRef.current); }, []);

    const poll = (id) => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await axios.get(`${API}/admin/analyst/jobs/${id}`);
                setJob(data);
                if (data.status !== 'pending') { clearInterval(pollRef.current); loadHistory(); if (data.status === 'error') toast.error(data.error || 'Analysis failed'); }
            } catch { clearInterval(pollRef.current); }
        }, 3000);
    };

    const run = async () => {
        const tfs = TFS.filter((tf) => slots[tf]);
        if (pair.replace(/[^a-z]/gi, '').length < 6) return toast.error('Enter a pair like GBPJPY');
        if (!tfs.length) return toast.error('Upload at least one chart screenshot');
        const fd = new FormData();
        fd.append('pair', pair); fd.append('note', note); fd.append('timeframes', tfs.join(','));
        tfs.forEach((tf) => fd.append('files', slots[tf]));
        try {
            const { data } = await axios.post(`${API}/admin/analyst/analyze`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setJob({ id: data.job_id, status: 'pending', pair: pair.toUpperCase() }); poll(data.job_id);
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not start analysis'); }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-5xl mx-auto px-4 py-10 space-y-8" data-testid="admin-analyst-page">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase font-bold mb-2" style={{ color: 'var(--primary-color)' }}>Chart Analyst</p>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>Screenshot → CRT analysis → annotated chart</h1>
                    <p className="text-sm mt-2 max-w-2xl" style={{ color: 'var(--text-secondary)' }}>Type the pair, upload your OANDA/TradingView screenshots from D1 down to M15 (as many as you have — D1, H4 and H1 give the best read), and the analyst applies the video-trained CRT rulebook. Exact prices come from live market data so levels are precise. Edit anything before publishing.</p>
                </div>
                <div className="rounded-2xl p-6 space-y-4" style={card}>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-1"><label className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Pair</label><Input value={pair} onChange={(e) => setPair(e.target.value.toUpperCase())} placeholder="GBPJPY" className="input-field font-mono" data-testid="analyst-pair-input" /></div>
                        <div className="sm:col-span-2"><label className="text-[11px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>Note for the analyst (optional)</label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. London session, NFP later today" className="input-field" data-testid="analyst-note-input" /></div>
                    </div>
                    <UploadSlots slots={slots} setSlots={setSlots} />
                    <Button className="btn-primary" onClick={run} disabled={job?.status === 'pending'} data-testid="analyst-run-btn">
                        {job?.status === 'pending' ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analysing (30–90s)…</> : <><ScanLine className="w-4 h-4 mr-2" />Analyse charts</>}
                    </Button>
                </div>
                {job?.status === 'done' && <Result job={job} onPublished={() => { setJob(null); loadHistory(); }} />}
                {!!history.length && (
                    <div data-testid="analyst-history">
                        <p className="text-xs font-black uppercase tracking-wide mb-3" style={{ color: 'var(--text-muted)' }}>Recent analyses</p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {history.map((h) => (
                                <button key={h.id} type="button" onClick={() => h.status === 'done' && setJob(h)} className="text-start rounded-xl p-4 flex items-center gap-3" style={card} data-testid={`analyst-history-${h.id}`}>
                                    {h.image_url ? <img src={`${API}/files/${h.image_url}`} alt="" className="w-16 h-10 object-cover rounded" /> : <div className="w-16 h-10 rounded flex items-center justify-center" style={{ backgroundColor: 'var(--primary-muted)' }}><ImageIcon className="w-4 h-4" style={{ color: 'var(--primary-color)' }} /></div>}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{h.pair} <span className="text-xs font-black" style={{ color: verdictColor[h.result?.verdict] || 'var(--text-muted)' }}>{h.result?.verdict || h.status}</span></p>
                                        <p className="text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>{new Date(h.created_at).toLocaleString()} · {(h.timeframes || []).join(' ')}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
